// @flow
import md5 from 'md5';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import {
  actions as authActions,
  getClientInactivityTimeout,
} from 'state/reducers/authReducer';
import { getAdminProperties } from './adminPropertiesCommands';
import { actions as serverActions } from 'state/reducers/serverReducer';
import type { Dispatch, GetState } from 'state/types';
import logger from 'portfolio-shared/util/logging';
import {
  getAdminSession,
  getBackupRestoreStatus,
  getIdentitySession,
  getIsFullAdmin,
  getLastActivity,
  getServerRunState,
} from 'state/selectors';
import {
  SERVER_STATE_STARTED,
  SERVER_STATE_STARTING,
  SERVER_STATE_STOPPING,
} from 'util/constants';
import { clearStateAction } from 'state/types/globalTypes';
import { handleError } from 'util/safeDispatch';
import PortfolioError from 'portfolio-shared/util/PortfolioError';
import { FC_INVALID_CREDENTIALS } from 'portfolio-shared/util/faultCode';
import localizedStrings from 'util/localizedStrings';
import { addLocales } from 'portfolio-shared/util/addLocales';
import { initLocalStorage } from 'portfolio-shared/util/persistenceUtils';
import { loadPersistedData } from 'state/commands/persistCommands';
import { STORAGE_KEY } from 'util/constants';
import { getLocale } from 'state/selectors';
import { setMessages } from 'state/commands/intlCommands';
import { isBackupRestoreBusy } from 'util/backupUtils';
import { encrypt } from 'util/serverUtils';

const {
  adminLogin,
  adminLogout,
  getPublicKeyInfo,
  identityLogin,
  identityLogout,
  setAdminPropertiesPollInterval,
} = authActions;
const { getServerSystemInformation, setServerStatus } = serverActions;

const POLL_INTERVAL_REGULAR = 10000;
const POLL_INTERVAL_FAST = 1000;
let pollTimer;

function computePollingInterval(getState) {
  const runState = getServerRunState(getState());
  return runState === SERVER_STATE_STARTING ||
    runState === SERVER_STATE_STOPPING ||
    isBackupRestoreBusy(getBackupRestoreStatus(getState()))
    ? POLL_INTERVAL_FAST
    : POLL_INTERVAL_REGULAR;
}

function handlePollingTimeout(dispatch, getState, isFirstTime) {
  return async () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    if (
      Date.now() - getLastActivity(getState()) >
      getClientInactivityTimeout()
    ) {
      dispatch(logout());
    } else {
      if (!isFirstTime && getAdminSession(getState())) {
        await dispatch(getAdminProperties(getPropertyListForState(getState())));
      }
      pollTimer = setTimeout(
        handlePollingTimeout(dispatch, getState),
        computePollingInterval(getState)
      );
    }
  };
}

export function login(username: string, unencryptedPassword: string) {
  return async function login(dispatch: Dispatch, getState: GetState) {
    const hashedPassword = md5(unencryptedPassword);
    await dispatch(adminLogin(username, hashedPassword)).catch(e => {
      logger.info('admin login failed, attempting identity service login');
    });
    const isFullAdmin = getIsFullAdmin(getState());
    let rsaEncryptedPassword;
    if (!isFullAdmin) {
      const publicKeyInfoResult = await dispatch(getPublicKeyInfo());
      rsaEncryptedPassword = encrypt(
        unencryptedPassword,
        publicKeyInfoResult.payload
      );
    }

    const loginAction = identityLogin(
      username,
      isFullAdmin ? hashedPassword : rsaEncryptedPassword,
      !isFullAdmin
    );

    await dispatch(loginAction).catch(e => {
      logger.info('identity login failed');
      if (!isFullAdmin) {
        handleError((e: PortfolioError), dispatch, {
          [FC_INVALID_CREDENTIALS]: localizedStrings.loginFailed,
        });
      }
    });

    const identitySessionId = getIdentitySession(getState());
    if (identitySessionId) {
      dispatch(setServerStatus(SERVER_STATE_STARTED));
      dispatch(getServerSystemInformation(identitySessionId));
    }
    if (isFullAdmin) {
      await dispatch(getAdminProperties(getPropertyListForState(getState())));
      // Begin Change Status polling
      dispatch(
        setAdminPropertiesPollInterval(
          handlePollingTimeout(dispatch, getState, true)
        )
      );
    }
    await dispatch(getAdminProperties([ADMIN_PROPERTIES.reachoutMessages]));
  };
}

export function logout() {
  return async function logout(dispatch: Dispatch, getState: GetState) {
    const {
      auth: { adminSessionId, identitySessionId },
    } = getState();
    if (adminSessionId) {
      await dispatch(adminLogout(adminSessionId)).catch(e => {
        logger.info('admin logout failed');
      });
    }
    if (identitySessionId) {
      await dispatch(identityLogout(identitySessionId)).catch(e => {
        logger.info('identity logout failed');
      });
    }

    // DESTROY ALL STATE
    dispatch(clearStateAction);

    // Need to reinitialize Intl
    addLocales(async () => {
      initLocalStorage(STORAGE_KEY);
      await dispatch(loadPersistedData());
      const locale = getLocale(getState());
      await dispatch(setMessages(locale));
    });
  };
}

function getPropertyListForState(state) {
  const properties = [
    ADMIN_PROPERTIES.backupRestoreStatus,
    ADMIN_PROPERTIES.diagnosticDataStatus,
    ADMIN_PROPERTIES.serverStatus,
    ADMIN_PROPERTIES.licenses,
    ADMIN_PROPERTIES.loggingConfig,
    ADMIN_PROPERTIES.ports,
    ADMIN_PROPERTIES.globalSettings,
  ];
  if (getIdentitySession(state)) {
    properties.push(ADMIN_PROPERTIES.mediaEngines);
    properties.push(ADMIN_PROPERTIES.globalStatistics);
  }
  return properties;
}



// WEBPACK FOOTER //
// ./src/state/commands/authCommands.js